import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Music, Headphones, Heart, ChevronRight, Check } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';

interface Artist {
  id: string;
  name: string;
  avatar: string;
  genre: string;
}

const genres = [
  'Worship',
  'Gospel',
  'Christian Hip-Hop',
  'Christian Pop',
  'Christian Rock',
  'Contemporary Christian',
  'Praise & Worship',
  'Christian R&B',
  'Southern Gospel',
  'Acoustic',
  'Hymns',
  'Soul',
];

export const OnboardingModal: React.FC = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [featuredArtists, setFeaturedArtists] = useState<Artist[]>([]);
  const [followedArtists, setFollowedArtists] = useState<Set<string>>(new Set());
  const [selectedGenres, setSelectedGenres] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    checkOnboardingStatus();
  }, [user]);

  const checkOnboardingStatus = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('onboarding_completed')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') throw error;

      if (!data || !data.onboarding_completed) {
        setIsOpen(true);
        loadFeaturedArtists();
      }
    } catch (error) {
      console.error('Error checking onboarding status:', error);
    }
  };

  const loadFeaturedArtists = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, display_name, username, avatar_url')
        .eq('is_artist', true)
        .limit(6);

      if (error) throw error;

      if (data) {
        const artists: Artist[] = data.map((profile) => ({
          id: profile.id,
          name: profile.display_name || profile.username || 'Artist',
          avatar: profile.avatar_url || 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=400&fit=crop',
          genre: 'Artist',
        }));
        setFeaturedArtists(artists);
      }
    } catch (error) {
      console.error('Error loading featured artists:', error);
    }
  };

  const toggleFollowArtist = async (artistId: string) => {
    if (!user) return;

    const newFollowed = new Set(followedArtists);
    if (newFollowed.has(artistId)) {
      newFollowed.delete(artistId);
      await supabase
        .from('user_follows')
        .delete()
        .eq('follower_id', user.id)
        .eq('following_id', artistId);
    } else {
      newFollowed.add(artistId);
      await supabase.from('user_follows').insert({
        follower_id: user.id,
        following_id: artistId,
      });
    }
    setFollowedArtists(newFollowed);
  };

  const toggleGenre = (genre: string) => {
    const newGenres = new Set(selectedGenres);
    if (newGenres.has(genre)) {
      newGenres.delete(genre);
    } else {
      newGenres.add(genre);
    }
    setSelectedGenres(newGenres);
  };

  const handleComplete = async () => {
    if (!user) return;

    setLoading(true);

    try {
      // Update user profile
      const { error: profileError } = await supabase
        .from('user_profiles')
        .upsert({
          user_id: user.id,
          onboarding_completed: true,
          favorite_genres: Array.from(selectedGenres),
          updated_at: new Date().toISOString(),
        });

      if (profileError) throw profileError;

      toast.success('Welcome to Vision Music!');
      setIsOpen(false);
    } catch (error) {
      console.error('Error completing onboarding:', error);
      toast.error('Failed to complete onboarding');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = async () => {
    if (!user) return;

    try {
      await supabase.from('user_profiles').upsert({
        user_id: user.id,
        onboarding_completed: true,
        updated_at: new Date().toISOString(),
      });

      setIsOpen(false);
    } catch (error) {
      console.error('Error skipping onboarding:', error);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="bg-white border border-stone-200 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
              <div className="sticky top-0 bg-white border-b border-stone-200 p-6 flex items-center justify-between rounded-t-2xl">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-royal-gold rounded-full flex items-center justify-center">
                    <Music className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-royal-text">Welcome to Vision Music</h2>
                </div>
                <button
                  onClick={handleSkip}
                  className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6 text-stone-400" />
                </button>
              </div>

              <div className="p-6">
                <AnimatePresence mode="wait">
                  {currentStep === 1 && (
                    <motion.div
                      key="step1"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="space-y-8"
                    >
                      <div className="text-center mb-8">
                        <h3 className="text-3xl font-bold text-royal-text mb-4">
                          Let's get you started
                        </h3>
                        <p className="text-royal-text-secondary">
                          Discover amazing music and support your favorite artists
                        </p>
                      </div>

                      <div className="grid md:grid-cols-3 gap-6">
                        <div className="bg-stone-50 border border-stone-200 rounded-xl p-6 text-center">
                          <div className="w-16 h-16 bg-royal-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Music className="w-8 h-8 text-royal-gold" />
                          </div>
                          <h4 className="font-semibold text-royal-text mb-2">
                            Unlimited Music
                          </h4>
                          <p className="text-sm text-royal-text-secondary">
                            Stream millions of songs from emerging and established artists
                          </p>
                        </div>

                        <div className="bg-stone-50 border border-stone-200 rounded-xl p-6 text-center">
                          <div className="w-16 h-16 bg-royal-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Headphones className="w-8 h-8 text-royal-gold" />
                          </div>
                          <h4 className="font-semibold text-royal-text mb-2">
                            High Quality Audio
                          </h4>
                          <p className="text-sm text-royal-text-secondary">
                            Experience crystal clear sound with our premium streaming quality
                          </p>
                        </div>

                        <div className="bg-stone-50 border border-stone-200 rounded-xl p-6 text-center">
                          <div className="w-16 h-16 bg-royal-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Heart className="w-8 h-8 text-royal-gold" />
                          </div>
                          <h4 className="font-semibold text-royal-text mb-2">
                            Support Artists
                          </h4>
                          <p className="text-sm text-royal-text-secondary">
                            Your streams directly support the artists you love
                          </p>
                        </div>
                      </div>

                      <button
                        onClick={() => setCurrentStep(2)}
                        className="w-full py-4 bg-royal-gold text-white font-bold rounded-xl hover:bg-royal-gold-dark transition-colors flex items-center justify-center gap-2"
                      >
                        Get Started
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    </motion.div>
                  )}

                  {currentStep === 2 && (
                    <motion.div
                      key="step2"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="space-y-8"
                    >
                      <div className="text-center mb-8">
                        <h3 className="text-3xl font-bold text-royal-text mb-4">
                          Follow Featured Artists
                        </h3>
                        <p className="text-royal-text-secondary">
                          Start building your music library with some of our featured artists
                        </p>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        {featuredArtists.map((artist) => {
                          const isFollowing = followedArtists.has(artist.id);
                          return (
                            <div
                              key={artist.id}
                              className="bg-stone-50 border border-stone-200 rounded-xl p-4 flex items-center gap-4"
                            >
                              <img
                                src={artist.avatar}
                                alt={artist.name}
                                className="w-16 h-16 rounded-full object-cover"
                              />
                              <div className="flex-1">
                                <h4 className="font-semibold text-royal-text">{artist.name}</h4>
                                <p className="text-sm text-royal-text-secondary">{artist.genre}</p>
                              </div>
                              <button
                                onClick={() => toggleFollowArtist(artist.id)}
                                className={`px-4 py-2 rounded-xl font-semibold transition-colors ${
                                  isFollowing
                                    ? 'bg-stone-100 border border-stone-200 text-royal-text hover:bg-stone-200'
                                    : 'bg-royal-gold text-white hover:bg-royal-gold-dark'
                                }`}
                              >
                                {isFollowing ? (
                                  <span className="flex items-center gap-2">
                                    <Check className="w-4 h-4" />
                                    Following
                                  </span>
                                ) : (
                                  'Follow'
                                )}
                              </button>
                            </div>
                          );
                        })}
                      </div>

                      <div className="flex gap-4">
                        <button
                          onClick={() => setCurrentStep(1)}
                          className="flex-1 py-4 bg-stone-100 border border-stone-200 text-royal-text font-semibold rounded-xl hover:bg-stone-200 transition-colors"
                        >
                          Back
                        </button>
                        <button
                          onClick={() => setCurrentStep(3)}
                          className="flex-1 py-4 bg-royal-gold text-white font-bold rounded-xl hover:bg-royal-gold-dark transition-colors flex items-center justify-center gap-2"
                        >
                          Continue
                          <ChevronRight className="w-5 h-5" />
                        </button>
                      </div>
                    </motion.div>
                  )}

                  {currentStep === 3 && (
                    <motion.div
                      key="step3"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="space-y-8"
                    >
                      <div className="text-center mb-8">
                        <h3 className="text-3xl font-bold text-royal-text mb-4">
                          Choose Your Genres
                        </h3>
                        <p className="text-royal-text-secondary">
                          Select the genres you love to personalize your experience
                        </p>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {genres.map((genre) => {
                          const isSelected = selectedGenres.has(genre);
                          return (
                            <button
                              key={genre}
                              onClick={() => toggleGenre(genre)}
                              className={`p-4 rounded-xl font-semibold transition-all ${
                                isSelected
                                  ? 'bg-royal-gold text-white scale-105 shadow-md'
                                  : 'bg-stone-50 border border-stone-200 text-royal-text hover:bg-stone-100'
                              }`}
                            >
                              {genre}
                            </button>
                          );
                        })}
                      </div>

                      <div className="flex gap-4">
                        <button
                          onClick={() => setCurrentStep(2)}
                          className="flex-1 py-4 bg-stone-100 border border-stone-200 text-royal-text font-semibold rounded-xl hover:bg-stone-200 transition-colors"
                        >
                          Back
                        </button>
                        <button
                          onClick={handleComplete}
                          disabled={loading}
                          className="flex-1 py-4 bg-royal-gold text-white font-bold rounded-xl hover:bg-royal-gold-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {loading ? 'Setting up...' : 'Start Listening'}
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <div className="flex items-center justify-center gap-2 p-6 border-t border-stone-200">
                {[1, 2, 3].map((step) => (
                  <div
                    key={step}
                    className={`h-2 rounded-full transition-all ${
                      currentStep === step ? 'bg-royal-gold w-8' : 'bg-stone-200 w-2'
                    }`}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
